import { BrowserRouter, Route, Routes } from '@bedrockio/router';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import SessionSwitch from 'helpers/SessionSwitch';
import { SessionProvider, useSession } from 'stores/session';

import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';

import LoadingScreen from 'screens/Loading';

import 'utils/sentry';
import { watchStuckLayers } from 'utils/stuckLayers';

import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';
import './styles/globals.css';

dayjs.extend(customParseFormat);

const App = React.lazy(() => import('./App.js'));
const AuthApp = React.lazy(() => import('./AuthApp.js'));
const OnboardApp = React.lazy(() => import('./OnboardApp.js'));
const Share = React.lazy(() => import('./screens/Share/index.jsx'));
const ShareFileFullView = React.lazy(() =>
  import('./screens/Render/index.jsx').then((module) => ({
    default: module.ShareFileFullView,
  })),
);

function AppSwitch() {
  const { user, loading } = useSession();
  if (loading) {
    return <LoadingScreen />;
  } else if (user) {
    // Any authenticated user enters the app — org membership decides
    // what they see inside, not whether they get in at all.
    return <App />;
  } else {
    return <AuthApp />;
  }
}

export default function Wrapper() {
  // Restores the page if a modal layer ever stalls while closing — see
  // utils/stuckLayers.js. Mounted once, at the root, for the whole app.
  React.useEffect(() => watchStuckLayers(), []);

  return (
    <SessionProvider>
      <ThemeProvider>
        <Toaster />
        <BrowserRouter>
          <HelmetProvider>
            <SessionSwitch>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {/* Public share links — no session required.
                      The wildcard makes every node inside a shared
                      folder addressable: /s/:token/specs/api.md. */}
                  {/* Before the wildcard, or it swallows this and the
                      share screen renders "not found" for a path that
                      was never a node. */}
                  <Route path="/s/:token/view/*path" render={ShareFileFullView} exact />
                  <Route path="/s/:token/view" render={ShareFileFullView} exact />
                  <Route path="/s/:token/*path" render={Share} />
                  <Route path="/s/:token" render={Share} exact />
                  <Route path="/onboard" render={OnboardApp} />
                  <Route path="/" render={AppSwitch} />
                </Routes>
              </Suspense>
            </SessionSwitch>
          </HelmetProvider>
        </BrowserRouter>
      </ThemeProvider>
    </SessionProvider>
  );
}
